// Operator's offer form — right column of /tarjouspyynnot/:id. Two-stage:
//   1. "Tallenna luonnos"    → POST /offers (status 'draft', customer can't see)
//                              + unlocks the attachment uploader
//   2. "Lähetä asiakkaalle"  → POST /offers/:id/send (draft → 'pending')
// Same semantics as puminet4's OfferForm; lifecycle flags + price guards come
// from @ibetoni/jerry-client. maintainsOrderInfo is hidden and pinned to
// false since 2026-07-18 (open-details model made the feature unused).

import { useState } from "react";
import {
    Alert, Box, Button, Collapse, Stack, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import SendIcon from "@mui/icons-material/Send";
import {
    centsToEuros, eurosToCents, isSubmittablePrice,
    isOfferDraft, isOfferLocked, defaultValidUntil, DEFAULT_PRICE_TERMS_FI,
} from "@ibetoni/jerry-client";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { providerApi } from "src/utils/tarjouspyynnot/providerApi";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import OfferAttachments from "src/components/tarjouspyynnot/OfferAttachments";
import { readOfferDraft, clearOfferDraft } from "src/utils/tarjouspyynnot/offerDraft";

export default function OfferForm({ pumppuRequestId, ownOffer, ownAttachments = [], onSubmitted = () => {} }) {
    const { token, logout } = useAuth();
    const { lang } = useLang();
    const t = useT();

    // Hydrate from the preview page's local draft ONLY when no server-side
    // offer exists — a server draft always wins (spec §4).
    const [price, setPrice] = useState(() => {
        const own = centsToEuros(ownOffer?.priceCents);
        if (own) return own;
        // A server offer row already exists — even with a null price, it must
        // win over the preview page's local draft (mirrors extraNotes below).
        if (ownOffer?.pumppuOfferId) return "";
        return readOfferDraft(pumppuRequestId)?.price || "";
    });
    const [extraNotes, setExtraNotes] = useState(() => {
        if (ownOffer?.extraNotes) return ownOffer.extraNotes;
        if (ownOffer?.pumppuOfferId) return "";
        return readOfferDraft(pumppuRequestId)?.extraNotes || "";
    });
    const [validUntil, setValidUntil] = useState(
        ownOffer?.validUntil ? String(ownOffer.validUntil).slice(0, 10) : defaultValidUntil(),
    );
    const [priceTerms, setPriceTerms] = useState(
        ownOffer?.pumppuOfferId ? (ownOffer.priceTerms || "") : DEFAULT_PRICE_TERMS_FI,
    );
    const [offerId, setOfferId] = useState(ownOffer?.pumppuOfferId || null);
    const [offerStatus, setOfferStatus] = useState(ownOffer?.status || null);
    const [attachments, setAttachments] = useState(ownAttachments);
    const [moreOpen, setMoreOpen] = useState(false);
    const [error, setError] = useState(null);
    const [busyAction, setBusyAction] = useState(null); // null | "save" | "send"

    const priceCents = eurosToCents(price);
    const submittable = isSubmittablePrice(priceCents);
    const isDraft = isOfferDraft(offerStatus);
    const locked = isOfferLocked(offerStatus);

    const handleSave = async () => {
        setBusyAction("save");
        setError(null);
        try {
            const body = {
                priceCents,
                extraNotes: extraNotes.trim() || undefined,
                validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
                priceTerms: priceTerms.trim() || undefined,
                maintainsOrderInfo: false, // feature hidden 2026-07-18 (open-details model) — pin false
            };
            const result = await providerApi(token).submitOffer(pumppuRequestId, body);
            if (!result.success) {
                setError(result.error || t("offers.saveFailed"));
                return;
            }
            setOfferId(result.data.pumppuOfferId);
            setOfferStatus(result.data.status);
            clearOfferDraft(pumppuRequestId); // server draft now owns the data
            onSubmitted(result.data);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "offers.saveFailed" }));
        } finally {
            setBusyAction(null);
        }
    };

    const handleSend = async () => {
        if (!offerId) return;
        setBusyAction("send");
        setError(null);
        try {
            const result = await providerApi(token).sendOffer(pumppuRequestId, offerId);
            if (!result.success) {
                setError(result.error || t("offers.sendFailed"));
                return;
            }
            setOfferStatus(result.data.status);
            onSubmitted(result.data);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "offers.sendFailed" }));
        } finally {
            setBusyAction(null);
        }
    };

    return (
        <Stack spacing={2}>
            {/* Price + its trust line are ONE unit. This number is the payload of
                the whole screen — the two fields below it are optional by
                comparison — so it carries the type weight. */}
            <Box>
                <TextField
                    label={t("provider.shared.priceLabel")}
                    helperText={t("provider.shared.priceHelper")}
                    type="number"
                    slotProps={{
                        htmlInput: { min: 1, step: 1, inputMode: "numeric" },
                        input: { sx: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.3 } },
                    }}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    fullWidth
                    required
                    disabled={locked}
                />

                {/* Deliberately here rather than at the foot of the form: pricing
                    is treated as confidential in the Finnish concrete pumping
                    trade, and the hesitation ("who else sees my number?") happens
                    at this input. An answer below the fold answers nobody.
                    Rendered in EVERY state — isOfferDraft(null) is true, so
                    gating the guarantee on !isDraft (as this did) hid it from
                    exactly the first-time provider who needed it, and showed it
                    only once the price had already been sent. */}
                <Stack direction="row" spacing={0.75} sx={{ alignItems: "flex-start", mt: 1 }}>
                    <LockIcon sx={{ fontSize: 16, color: "text.secondary", mt: "2px", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("provider.offerForm.privacyNotice")}
                        {isDraft && ` ${t("provider.offerForm.notSentYetNotice")}`}
                    </Typography>
                </Stack>
            </Box>

            <TextField
                label={t("provider.offerForm.priceTermsLabel")}
                value={priceTerms}
                onChange={(e) => setPriceTerms(e.target.value)}
                multiline
                rows={3}
                fullWidth
                disabled={locked}
            />

            <TextField
                label={t("provider.shared.extraNotesLabel")}
                helperText={t("provider.shared.extraNotesHelper")}
                value={extraNotes}
                onChange={(e) => setExtraNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                disabled={locked}
            />

            <Box>
                <Stack
                    direction="row"
                    onClick={() => setMoreOpen((o) => !o)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={moreOpen}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setMoreOpen((o) => !o);
                        }
                    }}
                    sx={{ alignItems: "center", cursor: "pointer", userSelect: "none" }}
                >
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>{t("provider.offerForm.extraOptionsLabel")}</Typography>
                    <ExpandMoreIcon
                        sx={{ transform: moreOpen ? "rotate(180deg)" : "none", transition: "transform 120ms", color: "text.secondary" }}
                    />
                </Stack>
                <Collapse in={moreOpen}>
                    <Stack spacing={2} sx={{ mt: 1.5 }}>
                        <TextField
                            label={t("provider.offerForm.validUntilLabel")}
                            type="date"
                            value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                            disabled={locked}
                        />
                    </Stack>
                </Collapse>
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("provider.offerForm.attachmentsLabel")}</Typography>
                <OfferAttachments
                    pumppuOfferId={locked ? null : offerId}
                    attachments={attachments}
                    onChange={setAttachments}
                />
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {offerStatus === "pending" && (
                <Alert severity="success">
                    {t("provider.offerForm.pendingNotice")}
                </Alert>
            )}

            {!locked && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={handleSave}
                        disabled={!submittable || busyAction !== null}
                        loading={busyAction === "save"}
                        size="large"
                        sx={{ flex: 1 }}
                    >
                        {offerId
                            ? (isDraft ? t("provider.offerForm.updateDraftCta") : t("provider.offerForm.updateOfferCta"))
                            : t("provider.offerForm.saveDraftCta")}
                    </Button>
                    {isDraft && offerId && (
                        <Button
                            variant="contained"
                            onClick={handleSend}
                            disabled={!submittable || busyAction !== null}
                            loading={busyAction === "send"}
                            loadingPosition="start"
                            size="large"
                            startIcon={<SendIcon />}
                            sx={[amberCtaSx, { flex: 1 }]}
                        >
                            {t("provider.offerForm.sendCta")}
                        </Button>
                    )}
                </Stack>
            )}
        </Stack>
    );
}
